import React from 'react';
import { CallbackRef } from 'use-is-in-viewport';

import { Product } from 'src/utils/helpers/product';
import { ProductRecommendationSection } from 'src/utils/analytics/trackers/internal-gtm-tracker/internal-gtm-tracker.types';

import { CarouselProductCard } from 'src/components/product-card';
import EmptyLoadingState from 'components/empty-loading-state';

import { useProductCarousel } from './use-product-carousel';

import { Content, Heading, GroupHeader, Slider, Item } from './product-carousel.styles';

const DEFAULT_MARGINS = {
  nudge: 100,
  mobile: 0,
  smallTablet: 0,
  tablet: 0,
  largeTablet: 0,
  desktop: 0,
};

export type ProductCarouselProps = {
  isMini?: boolean;
  heading: string;
  loading: boolean;
  minimumProducts?: number;
  products: Product[];
  trackerSection?: ProductRecommendationSection;
  viewportVisibility: {
    ref: CallbackRef;
    hasBeenVisible: boolean;
  };
};

export function ProductCarousel({
  heading,
  loading,
  isMini = false,
  minimumProducts = 0,
  products,
  trackerSection = ProductRecommendationSection.custom,
  viewportVisibility: { ref, hasBeenVisible },
}: ProductCarouselProps): JSX.Element | null {
  const { handleAddToCart, setInternalTrackerContext } = useProductCarousel();

  if (hasBeenVisible && !loading && products.length < minimumProducts) {
    return null;
  }

  return (
    <div ref={ref} style={{ minHeight: isMini ? '300px' : '420px' }}>
      {hasBeenVisible && (
        <EmptyLoadingState isEmpty={false} isLoading={loading} page='productCarousel'>
          <Content isMini={isMini}>
            <GroupHeader>
              <Heading isMini={isMini}>{heading}</Heading>
            </GroupHeader>

            <Slider isMini={isMini} disableButtons={isMini} margins={DEFAULT_MARGINS}>
              {products.map((product, index) => (
                <Item key={product.id}>
                  <CarouselProductCard
                    dimensions={{
                      mobile: 150,
                      desktop: 160,
                    }}
                    onClick={() => setInternalTrackerContext({ index, trackerSection })}
                    onQuickAddClick={() => {
                      setInternalTrackerContext({ index, trackerSection });
                      void handleAddToCart(product);
                    }}
                    product={product}
                    productIndex={index}
                  />
                </Item>
              ))}
            </Slider>
          </Content>
        </EmptyLoadingState>
      )}
    </div>
  );
}
